// Транспорт и хуки картотеки сертификатов РМРС/РКО (таблицы certs_* ядра). ТОЛЬКО ЧТЕНИЕ.
//
// Отдельный файл, а не api/client.ts + api/hooks.ts: там всё построено на эмуляции Bitrix REST
// (POST /api/bx с method/params), а здесь обычные GET-ручки ядра (/api/certs/*) со своими
// фильтрами. Разбор ответа и классы ошибок переиспользуем — заводить свои значило бы получить
// вторую обработку 401 и вторую страницу логина.
//
// ПИСАТЬ СЮДА НЕЛЬЗЯ. Источник картотеки — файл в репозитории портала 55, который собирает
// его собственный синк; правка из CRM была бы затёрта следующим переносом (см. шапку
// schema_certs.sql). PDF раздаёт портал, а не CRM: в ответах только ключ файла и готовая
// ссылка, и портал отдаёт файл ТОЛЬКО ВОШЕДШЕМУ — об этом экран говорит прямо.

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'
import { строкаЗапроса } from '../lib/certs'
import type { ФильтрыСертов } from '../lib/certs'

const БАЗА = '/api/certs'

/** Тот же разбор ответа, что в api/client.ts: 401 → на логин, остальное — ApiError. */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    // 400 от ядра — «такой фильтр обслужить нельзя». Пустая таблица читалась бы как
    // «такого сертификата нет» — самая дорогая ложь в этом разделе, поэтому поднимаем тот
    // же тип, что и остальные разделы: QueryState печатает по нему честное объяснение.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// ─────────── формы ответов ядра (см. certs_api.py) ───────────

export interface ПометкаОхвата {
  limited?: boolean
  partial?: boolean
  note?: string
}

interface ПометкаОхватаВОтвете {
  _scope?: ПометкаОхвата
}

export interface Сертификат {
  /** Ключ файла в бакете R2 — он же идентификатор записи. */
  r2_key: string
  /** Папка внутри CERTS_RKO: «РМРС/2024 - 2025», «РКО/2026». */
  folder: string | null
  file_name: string | null
  /** Модель/тип оборудования, как её прочла из PDF языковая модель. */
  model: string | null
  /** «РМРС» | «РКО» | «иное» | «RoHS» | null. */
  registr: string | null
  /** Год ДОКУМЕНТА, а не срок действия: срока в источнике нет вовсе. */
  year: number | null
  /** Заводские номера, как в PDF (с дублями, если модель прочла номер дважды). */
  serials: string[] | null
  /** Сколько РАЗЛИЧНЫХ номеров в свидетельстве — считает база, а не фронт. */
  serial_count: number | null
  size_bytes: number | null
  /** Контрольной суммы файла в индексе нет: заполнит будущий обход R2. */
  etag: string | null
  /** «portal_index» — вычитка модели, «manual» — поправлено человеком. */
  extract_source: string
  /** Коды неполноты, подписи к ним — в lib/certs.ts. */
  problems: string[] | null
  /** Когда модель читала PDF: в индексе не записано, поэтому обычно null. */
  extracted_at: string | null
  updated_at: string | null
  synced_at: string | null
  /** Готовая ссылка на PDF у портала 55. Собирает ядро: ключ надо кодировать целиком. */
  file_url: string
  /** Какие номера строки совпали с запросом — для подсветки. Отбор сделала база. */
  serial_matched?: string[]
}

/** Строка реестра РМРС, похожая по названию. ПОДСКАЗКА, а не подтверждение. */
export interface СледВРеестре {
  src_id: string
  product_name: string | null
  manufacturer: string | null
  manufacturer_country: string | null
  certificate_no: string | null
  certificate_type: string | null
  valid_from: string | null
  valid_to: string | null
  status: string | null
  doc_url: string | null
  expired: boolean | null
  /**
   * Границы совпадения чистые: слева и справа от найденной модели нет буквы или цифры.
   * false — модель нашлась ВНУТРИ другого обозначения («UPS-10» в «BOLID UPS-1000-SD»),
   * то есть это, скорее всего, другой прибор.
   */
  tight: boolean
}

export interface КарточкаСертификата extends Сертификат {
  content_hash: string | null
  src_file: string | null
  first_import_id: number | null
  last_import_id: number | null
  /** Другие документы на ту же модель. */
  same_model: Сертификат[]
  /** Другие документы, где встречается хотя бы один из тех же заводских номеров. */
  same_serials: Array<Сертификат & { shared: string[] }>
  /** След в реестре РМРС (p55_rs_products) по названию изделия. */
  registry: СледВРеестре[]
  /**
   * Сколько соседей и строк реестра ЕСТЬ на самом деле и сколько не показано.
   *
   * Карточка обрезает списки (соседей 25, реестра 12), и до 28.07.2026 обрезка была
   * НЕВИДИМОЙ: при шестидесяти соседях человек видел двадцать пять и решал, что их
   * двадцать пять. Живьём сегодня обрезка не срабатывает — максимум 12 соседей, — но
   * молчащий предел однажды соврёт ровно в тот день, когда сработает.
   */
  same_model_total?: number
  same_model_hidden?: number
  same_serials_total?: number
  same_serials_hidden?: number
  registry_total?: number
  registry_hidden?: number
  neighbours_limit?: number
  registry_limit?: number
  registry_searched: boolean
  /** Почему в реестре не искали (короткая модель, не прочитанная модель). */
  registry_why_not: string
  data_notes: Record<string, string>
}

export interface СтраницаСертов extends ПометкаОхватаВОтвете {
  rows: Сертификат[]
  total: number
  next: number | null
  limit: number
  start: number
  /** «нет» | «с начала строки» | «внутри строки» — как искали по модели. */
  search_model: string
  /** «нет» | «точное совпадение» | «внутри номера» — как искали по номеру. */
  search_serial: string
  data_notes: Record<string, string>
}

export interface ПараКоличество {
  k: string | number | null
  n: number
}

export interface ПереносСертов {
  id: number
  started_at: string | null
  finished_at: string | null
  src_file: string | null
  /** Время правки файла индекса — «данные от такого-то числа». */
  src_mtime: string | null
  src_sha1: string | null
  files_seen: number | null
  rows_new: number | null
  rows_changed: number | null
  rows_same: number | null
  rows_gone: number | null
  serials_total: number | null
  /** Сводка переноса: что неполно, чего в источнике нет, что не покрыто. */
  notes: {
    problems?: Record<string, number>
    absent_in_source?: Record<string, string>
    not_covered?: {
      portal_nextcloud_certs?: number
      portal_type_approvals?: number
      note?: string
    }
    warnings?: string[]
    [k: string]: unknown
  } | null
  ok: boolean
}

export interface СправочникиСертов extends ПометкаОхватаВОтвете {
  counts: {
    docs: number
    with_serials: number
    without_serials: number
    serials: number
    serials_unique: number
    models: number
    manual: number
    size_bytes: number
    deleted: number
    synced_at: string | null
  }
  by_registr: ПараКоличество[]
  by_year: ПараКоличество[]
  by_folder: ПараКоличество[]
  problems: ПараКоличество[]
  last_import: ПереносСертов | null
  file_url_base: string
  /** true — портал отдаст PDF только вошедшему. Молчать об этом нельзя. */
  file_needs_portal_login: boolean
  page: number
  data_notes: Record<string, string>
}

// ─────────── хуки ───────────
// staleTime большой: картотека меняется разовым переносом (когда в папку добавили
// свидетельство), а не синком. Чаще спрашивать нечего.

const общее = { retry: 1, staleTime: 5 * 60_000 } as const

export const useСертыRefs = () =>
  useQuery({
    queryKey: ['certs', 'refs'],
    queryFn: () => запрос<СправочникиСертов>('/refs'),
    staleTime: 30 * 60_000,
    retry: 1,
  })

export const useСертификаты = (ф: ФильтрыСертов) =>
  useQuery({
    queryKey: ['certs', 'list', ф],
    queryFn: () => запрос<СтраницаСертов>('/list' + строкаЗапроса(ф)),
    // Чтобы таблица не мигала пустотой, пока едет следующая страница фильтра.
    placeholderData: (prev) => prev,
    ...общее,
  })

export const useСертификат = (ключ: string | undefined) =>
  useQuery({
    queryKey: ['certs', 'doc', ключ],
    queryFn: () => запрос<{ row: КарточкаСертификата }>(
      `/doc?key=${encodeURIComponent(ключ!)}`).then((r) => r.row),
    enabled: !!ключ,
    ...общее,
  })
